import {
  Button,
  Col,
  Divider,
  Dropdown,
  Menu,
  notification,
  Row,
  Typography,
} from "antd";
import { $journalApi } from "@/entities/journal/fetch/fetch-client";
import { RepresentationModal } from "@/features/representation-modal";
import { Can } from "@/shared/lib/casl";
import { fallbackString, NOTIFICATION_MESSAGES } from "@/shared/utils";
import type { components } from "@entities/journal";
import { journalsQueries } from "@entities/journal";
import { useDisclosure } from "@shared/lib/hooks";
import { queryClient } from "@shared/lib/react-query";
import {
  generatePartName,
  getTextColor,
  mapPartToPathSegment,
  TABLE_COLUMNS,
} from "../model";
import type { SettingsCardPartProps } from "../model";
import "./settings-card-part.scss";

type SignerUserInfoExtended = NonNullable<
  components["schemas"]["SignerInfo"]["userInfo"]
> & {
  userEmail?: string;
  userMobilePhone?: string;
};

export const SettingsCardPart = ({
  part,
  userList,
  id,
  section,
  singersList,
}: SettingsCardPartProps) => {
  const { isOpen, onClose, onOpen } = useDisclosure();

  const partSegment = mapPartToPathSegment(part);
  const mutationPath =
    `/api/v1/journals/${section}/${id}/${partSegment}` as const;

  const { mutateAsync: mutateAdd, isPending: isPendingAdd } =
    // @ts-expect-error динамический путь мутации openapi по разделу и роли
    $journalApi.useMutation("post", mutationPath);

  const { mutateAsync: mutateDelete } =
    // @ts-expect-error динамический путь мутации openapi по разделу и роли
    $journalApi.useMutation("delete", mutationPath);

  const addUser = async (userId: string) => {
    await mutateAdd({
      // @ts-expect-error body зависит от динамического пути мутации
      body: { userId },
    })
      .then(() => {
        onClose();
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getOriginalResponsibleList(id || "").queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getResponsibleSectionOneList(id || "").queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getResponsibleCommentListSecondOne(id || "").queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getResponsibleSectionTwoList(id || "").queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getResponsibleSectionThreeList(id || "").queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getResponsibleSectionFourList(id || "").queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getResponsibleSectionSixList(id || "").queryKey,
        });
        notification.success({ description: NOTIFICATION_MESSAGES.EMPLOYEE_ADDED_DESC, /* "Сотрудник добавлен" */ message: NOTIFICATION_MESSAGES.SUCCESS /* "Успешно" */ });
      })
      .catch((r) => {
        notification.error({
          description: r.errorDescription,
          message: NOTIFICATION_MESSAGES.EMPLOYEE_ADD_ERROR, // "Ошибка при добавлении сотрудника"
        });
      });
  };

  const deleteUser = async (userId: string) => {
    await mutateDelete({ body: { userId } })
      .then(() => {
        onClose();
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getOriginalResponsibleList(id || "").queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getResponsibleSectionOneList(id || "").queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getResponsibleCommentListSecondOne(id || "").queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getResponsibleSectionTwoList(id || "").queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getResponsibleSectionThreeList(id || "").queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getResponsibleSectionFourList(id || "").queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getResponsibleSectionSixList(id || "").queryKey,
        });
        notification.success({ description: NOTIFICATION_MESSAGES.EMPLOYEE_RIGHT_PAUSED_DESC, /* "Приостановлено право" */ message: NOTIFICATION_MESSAGES.SUCCESS /* "Успешно" */ });
      })
      .catch((r) => {
        notification.error({
          description: r.errorDescription,
          message: NOTIFICATION_MESSAGES.EMPLOYEE_RIGHT_PAUSE_ERROR, // "Ошибка при приостановлении права"
        });
      });
  };

  return (
    <div className="settings-card-part">
      <div className="settings-card-part-header">
        <Typography.Title level={5} style={{ marginBottom: 0 }}>
          {generatePartName(part)}
        </Typography.Title>
        {userList.length > 0 && part !== "signers" && (
          <Can I="access" a="main_journals">
            <Button type="link" onClick={onOpen}>
              Добавить
            </Button>
          </Can>
        )}
      </div>

      {userList.length === 0 && part !== "signers" && (
        <Can I="access" a="main_journals">
          <Button
            style={{ alignSelf: "self-start" }}
            type="primary"
            onClick={onOpen}
          >
            Добавить сотрудника
          </Button>
        </Can>
      )}

      {userList.length > 0 && (
        <>
          <Row className="table-header" gutter={[16, 16]}>
            {TABLE_COLUMNS.map((col) => (
              <Col key={col.title} span={col.span}>
                <Typography.Text style={{ color: "#666666" }}>
                  {col.title}
                </Typography.Text>
              </Col>
            ))}
          </Row>

          {userList.map((user) => (
            <Row
              key={user.userHistory?.userId}
              style={{ color: getTextColor(user) }}
              className="settings-card-part-user-row"
              gutter={[16, 16]}
              align="middle"
            >
              <Col span={5}>
                {`${user.userHistory?.userSurname} ${user.userHistory?.userFirstName} ${user.userHistory?.userMiddleName}`}
              </Col>
              <Col span={5}>
                {fallbackString(
                  user.userHistory?.roleInfo?.userCompaniesHistory?.[0]?.shortName,
                )}
              </Col>
              <Col span={5}>
                {fallbackString(
                  user.userHistory?.roleInfo?.userCompaniesHistory?.[0]?.position,
                )}
              </Col>
              <Col span={5}>{fallbackString(user.userHistory?.userEmail)}</Col>
              <Col span={3}>{fallbackString(user.userHistory?.userMobilePhone)}</Col>
              <Col span={1} style={{ textAlign: "right" }}>
                <Can I="access" a="main_journals">
                  <Dropdown
                    overlay={
                      <Menu>
                        {user.userHistory?.roleInfo?.isCurrently && (
                          <Menu.Item
                            onClick={() =>
                              deleteUser(user.userHistory?.userId as string)
                            }
                          >
                            Приостановить право
                          </Menu.Item>
                        )}
                        {!user.userHistory?.roleInfo?.isCurrently && (
                          <Menu.Item
                            onClick={() =>
                              addUser(user.userHistory?.userId as string)
                            }
                          >
                            Восстановить право
                          </Menu.Item>
                        )}
                      </Menu>
                    }
                    trigger={["hover"]}
                  >
                    <Button
                      type="text"
                      style={{ backgroundColor: "#EAEEF8" }}
                      icon={
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M9 4C9 4.55228 8.55228 5 8 5C7.44772 5 7 4.55228 7 4C7 3.44772 7.44772 3 8 3C8.55228 3 9 3.44772 9 4Z"
                            fill="#333333"
                          />
                          <path
                            d="M9 8C9 8.55228 8.55228 9 8 9C7.44772 9 7 8.55228 7 8C7 7.44772 7.44772 7 8 7C8.55228 7 9 7.44772 9 8Z"
                            fill="#333333"
                          />
                          <path
                            d="M9 12C9 12.5523 8.55228 13 8 13C7.44772 13 7 12.5523 7 12C7 11.4477 7.44772 11 8 11C8.55228 11 9 11.4477 9 12Z"
                            fill="#333333"
                          />
                        </svg>
                      }
                    />
                  </Dropdown>
                </Can>
              </Col>
            </Row>
          ))}
        </>
      )}

      {section === "title" && singersList && singersList.length > 0 ? (
        <Divider style={{ margin: 0 }} />
      ) : null}
      {section === "title" && singersList && singersList.length > 0 && (
        <>
          <div className="settings-card-part__signers-title">Подписанты</div>
          {singersList?.map((signer, idx) => {
            const userInfo = signer.userInfo as SignerUserInfoExtended | undefined;

            return (
            <Row
              key={signer.userInfo?.userId || idx}
              gutter={[16, 16]}
              className="settings-card-part-user-row"
              align="middle"
              style={{
                color: "#000000",
                paddingTop: 8,
                paddingBottom: 8,
                marginBottom: 8,
              }}
            >
              <Col span={5}>
                {`${signer.userInfo?.userFirstName || ""} ${signer.userInfo?.userSurname || ""} ${signer.userInfo?.userMiddleName || ""}`}
              </Col>
              <Col span={5}>{signer.representativeRole || "-"}</Col>
              <Col span={5}>{userInfo?.userEmail || "-"}</Col>
              <Col span={5}>{userInfo?.userMobilePhone || "-"}</Col>
              <Col span={4}>-</Col>
            </Row>
            );
          })}
        </>
      )}

      <RepresentationModal
        isOpen={isOpen}
        title={`Добавление ${generatePartName(part)}`}
        handleClose={onClose}
        handleAdd={(r) => addUser(r as string)}
        isLoading={isPendingAdd}
      />
    </div>
  );
};
